import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, CalendarDays, List, AlertCircle } from 'lucide-react';
import { CalendarOccurrence, CalendarScope } from '../../types';
import { requestOpenDetail, requestOpenEditor, useCalendarRange } from '../../services/calendarService';
import {
  addDaysKey, formatDateKo, isValidDateKey, itemsByDay, keyParts, monthGridKeys, monthGridRange, monthLabel, pad2, shiftMonth, todayKey,
} from '../../utils/calendarDate';
import { MonthGrid } from './MonthGrid';
import { AgendaList } from './AgendaList';
import { CalendarFilters, SCOPE_STYLE, readFilters, writeFilters } from './scopeStyles';

export interface CalendarFocusRequest {
  date: string;
  eventId?: string | null;
  seq: number;
}

interface CalendarScreenProps {
  /** wide = 데스크톱 넓은 배치(rail + 캘린더 한 칸) / stack = 모바일·좁은 PC앱 */
  layout: 'wide' | 'stack';
  focusRequest?: CalendarFocusRequest | null;
}

const VIEW_KEY = 'calendarView';
type View = 'month' | 'list';

function readView(): View {
  try { return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'month'; } catch { return 'month'; }
}

function scopeOf(o: CalendarOccurrence): CalendarScope {
  return o.source === 'intranet' ? 'dept' : o.scope;
}

// 처리한 이동 요청 — 탭을 오가며 화면이 다시 마운트돼도 같은 요청을 다시 적용하지 않는다(ActivityPanel requestedTab과 같은 방식)
let consumedFocusSeq = 0;

/**
 * 캘린더 메뉴 — 월 보기 / 목록 보기, 분류 필터(내 일정·부서·회사), 일정 추가.
 *
 * 데이터는 calendarService.useCalendarRange(월 격자 42일) 하나다. 홈 미니 캘린더와 같은 캐시 키라 같은 달이면 요청이 0이다.
 * 상세·편집 창은 App 루트 CalendarModalsHost가 연다(여기서 Modal을 직접 그리지 않는다).
 * 날짜는 KST 고정(calendarDate) — 기기 시간대가 달라도 칸이 밀리지 않는다.
 */
export const CalendarScreen: React.FC<CalendarScreenProps> = ({ layout, focusRequest }) => {
  const today = todayKey();
  const initial = (() => {
    if (focusRequest && focusRequest.seq > consumedFocusSeq && isValidDateKey(focusRequest.date)) return focusRequest.date;
    return today;
  })();
  const [cursor, setCursor] = useState(() => { const p = keyParts(initial); return { y: p.y, m: p.m }; });
  const [selected, setSelected] = useState<string>(initial);
  const [view, setView] = useState<View>(readView);
  const [filters, setFilters] = useState<CalendarFilters>(readFilters);
  const wide = layout === 'wide';

  // 알림·홈 카드·공유 카드에서 온 이동 요청
  useEffect(() => {
    if (!focusRequest || focusRequest.seq <= consumedFocusSeq) return;
    consumedFocusSeq = focusRequest.seq;
    if (!isValidDateKey(focusRequest.date)) return;
    const p = keyParts(focusRequest.date);
    setCursor({ y: p.y, m: p.m });
    setSelected(focusRequest.date);
    if (focusRequest.eventId) {
      requestOpenDetail({ eventId: String(focusRequest.eventId), date: focusRequest.date, fromCalendar: true });
    }
  }, [focusRequest]);

  const gridKeys = useMemo(() => monthGridKeys(cursor.y, cursor.m), [cursor.y, cursor.m]);
  const range = useMemo(() => monthGridRange(cursor.y, cursor.m), [cursor.y, cursor.m]);
  const { data, loading, error, refresh } = useCalendarRange(range.from, range.to);

  const occurrences = useMemo(
    () => (data?.occurrences || []).filter(o => filters[scopeOf(o)]),
    [data, filters],
  );
  const holidays = data?.holidays || {};
  const byDay = useMemo(() => itemsByDay(occurrences, range.from, range.to), [occurrences, range.from, range.to]);

  const goMonth = useCallback((delta: number) => {
    const next = shiftMonth(cursor.y, cursor.m, delta);
    const t = keyParts(today);
    setCursor(next);
    setSelected(t.y === next.y && t.m === next.m ? today : `${next.y}-${pad2(next.m)}-01`);
  }, [cursor.y, cursor.m, today]);

  const goToday = useCallback(() => {
    const t = keyParts(today);
    setCursor({ y: t.y, m: t.m });
    setSelected(today);
  }, [today]);

  const selectDay = useCallback((key: string) => {
    setSelected(key);
    const p = keyParts(key);
    setCursor(prev => (prev.y === p.y && prev.m === p.m ? prev : { y: p.y, m: p.m }));
  }, []);

  const openOcc = useCallback((o: CalendarOccurrence) => {
    requestOpenDetail({ eventId: o.eventId, date: o.startDate, fromCalendar: true });
  }, []);
  const createAt = useCallback((key: string) => requestOpenEditor({ date: key }), []);

  const toggleFilter = (s: CalendarScope) => {
    setFilters(prev => {
      const next = { ...prev, [s]: !prev[s] };
      writeFilters(next);
      return next;
    });
  };
  const changeView = (v: View) => {
    setView(v);
    try { localStorage.setItem(VIEW_KEY, v); } catch { /* 무시 */ }
  };

  // 이 달의 날짜들(목록 보기)
  const monthDays = useMemo(() => {
    const out: string[] = [];
    let k = `${cursor.y}-${pad2(cursor.m)}-01`;
    while (keyParts(k).m === cursor.m) { out.push(k); k = addDaysKey(k, 1); }
    return out;
  }, [cursor.y, cursor.m]);

  const notices: string[] = [];
  if (data?.deptUnavailable) notices.push('부서 정보를 확인하지 못해 부서 일정을 표시하지 못했습니다');
  if (data?.intranet === 'unavailable') notices.push('인트라넷 부서별일정을 불러오지 못했습니다');
  if (data?.truncated) notices.push('일정이 많아 일부만 표시합니다');

  const header = (
    <div className={`flex items-center gap-2 ${wide ? 'flex-wrap px-6 pt-5 pb-3' : 'px-2 pt-3 pb-2'}`}>
      <div className={`flex items-center min-w-0 ${wide ? 'gap-1' : 'gap-0.5'}`}>
        <button type="button" onClick={() => goMonth(-1)} aria-label="이전 달" className="w-9 h-10 md:h-9 shrink-0 rounded-lg flex items-center justify-center text-slate-600 hover:bg-slate-100">
          <ChevronLeft size={20} aria-hidden="true" />
        </button>
        <h1 className={`${wide ? 'text-xl' : 'text-lg'} font-extrabold tracking-tight text-slate-800 tabular-nums whitespace-nowrap`} aria-live="polite">
          {monthLabel(cursor.y, cursor.m)}
        </h1>
        <button type="button" onClick={() => goMonth(1)} aria-label="다음 달" className="w-9 h-10 md:h-9 shrink-0 rounded-lg flex items-center justify-center text-slate-600 hover:bg-slate-100">
          <ChevronRight size={20} aria-hidden="true" />
        </button>
        <button type="button" onClick={goToday} className="ml-1 shrink-0 h-9 px-3 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-600 hover:bg-slate-50">
          오늘
        </button>
      </div>
      <div className="flex-1" />
      <div className="flex items-center gap-1.5">
        {wide ? (
          <div className="flex rounded-lg border border-slate-200 bg-white p-0.5" role="group" aria-label="보기">
            {([['month', '월', CalendarDays], ['list', '목록', List]] as Array<[View, string, typeof List]>).map(([v, label, Icon]) => (
              <button
                key={v}
                type="button"
                aria-pressed={view === v}
                onClick={() => changeView(v)}
                className={`h-8 px-2.5 rounded-md text-[13px] font-semibold flex items-center gap-1 transition-colors ${view === v ? 'bg-slate-100 text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}
              >
                <Icon size={15} aria-hidden="true" />
                {label}
              </button>
            ))}
          </div>
        ) : (
          // 좁은 화면은 한 줄에 다 들어가야 해서(390px) 다른 보기로 바꾸는 버튼 하나만 둔다
          <button
            type="button"
            onClick={() => changeView(view === 'month' ? 'list' : 'month')}
            aria-label={view === 'month' ? '목록으로 보기' : '달력으로 보기'}
            title={view === 'month' ? '목록으로 보기' : '달력으로 보기'}
            className="w-10 h-10 rounded-lg border border-slate-200 bg-white flex items-center justify-center text-slate-600 hover:bg-slate-50"
          >
            {view === 'month' ? <List size={18} aria-hidden="true" /> : <CalendarDays size={18} aria-hidden="true" />}
          </button>
        )}
        <button
          type="button"
          onClick={() => requestOpenEditor({ date: selected })}
          aria-label="일정 추가"
          className={`h-9 ${wide ? 'pl-2.5 pr-3.5' : 'w-10 justify-center'} rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-bold flex items-center gap-1 transition-colors`}
        >
          <Plus size={18} aria-hidden="true" />
          {wide && '일정 추가'}
        </button>
      </div>
    </div>
  );

  const filterBar = (
    <div className={`flex items-center gap-1.5 overflow-x-auto ${wide ? 'px-6 pb-3' : 'px-3 pb-2'}`} role="group" aria-label="표시할 일정">
      {(['personal', 'dept', 'company'] as CalendarScope[]).map(s => (
        <button
          key={s}
          type="button"
          aria-pressed={filters[s]}
          onClick={() => toggleFilter(s)}
          className={`shrink-0 h-8 px-2.5 rounded-full border text-[13px] font-semibold flex items-center gap-1.5 transition-colors ${
            filters[s] ? SCOPE_STYLE[s].filterOn : 'bg-white border-slate-200 text-slate-400'
          }`}
        >
          <span aria-hidden="true" className={`w-2 h-2 rounded-full ${filters[s] ? SCOPE_STYLE[s].dot : 'bg-slate-300'}`} />
          {s === 'dept' && data?.deptName ? data.deptName : SCOPE_STYLE[s].label}
        </button>
      ))}
      {notices.length > 0 && (
        <span className="shrink-0 ml-1 flex items-center gap-1 text-xs text-slate-500">
          <AlertCircle size={13} aria-hidden="true" />
          {notices.join(' · ')}
        </span>
      )}
    </div>
  );

  const errorBox = error && !data ? (
    <div className="mx-3 md:mx-6 my-6 rounded-xl border border-slate-200 bg-white p-4">
      <p className="text-sm font-semibold text-slate-700">일정을 불러오지 못했습니다</p>
      <p className="mt-1 text-[13px] text-slate-500">잠시 후 다시 시도해 주세요</p>
      <button type="button" onClick={refresh} className="mt-3 h-9 px-3 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">
        다시 시도
      </button>
    </div>
  ) : null;

  const selectedItems = byDay[selected] || [];
  const dayPanel = (
    <div className="flex flex-col min-h-0">
      <div className="flex items-baseline gap-2 px-2 pb-1">
        <h2 className="text-sm font-bold text-slate-700">{selected === today ? `오늘 · ${formatDateKo(selected)}` : formatDateKo(selected)}</h2>
        {holidays[selected] && <span className="text-xs text-slate-500">{holidays[selected]}</span>}
      </div>
      <AgendaList
        days={[selected]}
        byDay={byDay}
        holidays={holidays}
        today={today}
        onOpen={openOcc}
        showEmptyDays
        hideDayHeader
        emptyTitle="일정이 없습니다"
        emptyBody="일정 추가로 이 날의 일정을 등록해 보세요"
        compact={!wide}
      />
      {selectedItems.length > 0 && (
        <button
          type="button"
          onClick={() => createAt(selected)}
          className="mt-1 mx-2 self-start h-8 px-2.5 rounded-lg text-[13px] font-semibold text-slate-600 hover:bg-slate-100 flex items-center gap-1"
        >
          <Plus size={14} aria-hidden="true" />
          이 날에 일정 추가
        </button>
      )}
    </div>
  );

  const listView = (
    <AgendaList
      days={monthDays}
      byDay={byDay}
      holidays={holidays}
      today={today}
      onOpen={openOcc}
      emptyTitle={`${cursor.m}월 일정이 없습니다`}
      emptyBody="일정 추가로 첫 일정을 등록해 보세요"
    />
  );

  const loadingBox = loading && !data ? (
    <div className="px-6 py-6 space-y-2" aria-busy="true">
      <div className="h-5 w-1/3 rounded bg-slate-100 animate-pulse" />
      <div className="h-40 rounded-xl bg-slate-100 animate-pulse" />
    </div>
  ) : null;

  if (wide) {
    return (
      <div className="h-full min-h-0 flex flex-col bg-slate-50">
        {header}
        {filterBar}
        {errorBox || loadingBox || (
          view === 'month' ? (
            <div className="flex-1 min-h-0 flex gap-4 px-6 pb-6">
              <div className="flex-1 min-w-0 overflow-y-auto rounded-2xl border border-slate-100 bg-white shadow-sm">
                <MonthGrid
                  gridKeys={gridKeys}
                  month={cursor.m}
                  occurrences={occurrences}
                  holidays={holidays}
                  today={today}
                  selected={selected}
                  onSelectDay={selectDay}
                  onOpenOccurrence={openOcc}
                  onCreateAt={createAt}
                  variant="bars"
                />
                {/* lg 미만에서는 오른쪽 패널이 없다 — 고른 날의 일정은 격자 아래에 */}
                <div className="lg:hidden border-t border-slate-100 p-3">{dayPanel}</div>
              </div>
              <aside className="hidden lg:flex w-80 shrink-0 flex-col min-h-0 overflow-y-auto rounded-2xl border border-slate-100 bg-white shadow-sm p-3" aria-label="선택한 날의 일정">
                {dayPanel}
              </aside>
            </div>
          ) : (
            <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6">
              <div className="max-w-3xl rounded-2xl border border-slate-100 bg-white shadow-sm p-3">
                {listView}
              </div>
            </div>
          )
        )}
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 flex flex-col bg-white">
      {header}
      {filterBar}
      <div className="flex-1 min-h-0 overflow-y-auto pb-6">
        {errorBox || loadingBox || (
          view === 'month' ? (
            <>
              <div className="px-2">
                <MonthGrid
                  gridKeys={gridKeys}
                  month={cursor.m}
                  occurrences={occurrences}
                  holidays={holidays}
                  today={today}
                  selected={selected}
                  onSelectDay={selectDay}
                  variant="dots"
                />
              </div>
              <div className="mt-2 mx-2 pt-3 border-t border-slate-100">{dayPanel}</div>
            </>
          ) : (
            <div className="px-1">{listView}</div>
          )
        )}
      </div>
    </div>
  );
};
